import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, GraduationCap, ShieldCheck, Users2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1757405930202-b2c3e11570fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBoZWFsdGhjYXJlfGVufDB8fHx8MTc4MTI4MDg5N3ww&ixlib=rb-4.1.0&q=85';

const indicatorIcons = [GraduationCap, ShieldCheck, Users2];

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="hero"
      data-testid={SITE.hero}
      className="relative overflow-hidden bg-[#0B0F0C] text-white pt-28 lg:pt-36 pb-20 lg:pb-28"
    >
      {/* Decorative grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(34,197,94,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.6) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(0,166,81,0.25) 0%, transparent 60%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
            <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#22C55E]">
              {t.hero.label}
            </span>
          </motion.div>

          <motion.h1
            data-testid={SITE.heroTitle}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-6 font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight"
          >
            {t.hero.title1}
            <br />
            <span className="text-white/95">{t.hero.title2}</span>
            <br />
            <span className="text-[#22C55E]">{t.hero.title3}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-2xl text-base lg:text-lg text-white/70 leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#solution"
              data-testid={SITE.heroCtaPrimary}
              className="group inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#22C55E] text-white font-semibold py-3.5 px-7 rounded-xl transition-all duration-300 hover:-translate-y-0.5 shadow-[0_12px_30px_-8px_rgba(34,197,94,0.55)]"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#contact"
              data-testid={SITE.heroCtaSecondary}
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/15 hover:border-[#22C55E] text-white font-semibold py-3.5 px-7 rounded-xl transition-all duration-300"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          {/* Mini indicators */}
          <motion.ul
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl"
          >
            {t.hero.indicators.map((label, i) => {
              const Icon = indicatorIcons[i];
              return (
                <li
                  key={label}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-sm"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-[#22C55E]/10 flex items-center justify-center">
                    <Icon size={18} className="text-[#22C55E]" />
                  </div>
                  <span className="text-sm text-white/85 leading-snug">{label}</span>
                </li>
              );
            })}
          </motion.ul>
        </div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.55)]">
            <img
              src={HERO_IMAGE}
              alt={t.hero.imageAlt}
              className="w-full h-[420px] sm:h-[480px] lg:h-[560px] object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to top, rgba(11,15,12,0.75) 0%, rgba(11,15,12,0.0) 45%)',
              }}
            />
            {/* Floating tag */}
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 border border-white/40">
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#00A651]">
                E-Gestionnaire
              </div>
              <div className="mt-1 font-['Outfit'] font-semibold text-[#0B0F0C]">
                Formation · Accompagnement · Conformité
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
